import {
  all, call, put, takeLatest,
} from 'redux-saga/effects';
import qs from 'qs';
import { setSuccessMessage, setErrorMessage, changeEndedStatus } from 'app/settings/slices';
import {
  changeFileManagerLoadingState,
  saveBaseDirectories,
  changeModalVisibility,
  saveDirectories,
  getFileTypeGroupsSuccess,
  changeFileManagerBrandId,
  changeAllDirectoriesLoadingState,
  changePopoverVisibility,
  saveFilesByPaths,
  changeDirectoriesEndedStatus,
} from 'actions/fileManager/fileManagerActionCreator';
import NetworkService from 'services/networkService';
import FileManagerActionType from 'actions/fileManager/fileManagerActionType';
import { i18n } from 'services';
import { AppConstants } from 'constants/index';
import { getError, getMessage } from 'utils/helpers';
import { isJSON } from 'utils/utils';

function* getDirectories({ payload }) {
  const { params } = payload;
  try {
    const options = {
      params,
      paramsSerializer: (param) => qs.stringify(param),
    };
    const { BrandAdmin, FileManager, Directories } = AppConstants.api;
    yield put(changeAllDirectoriesLoadingState(true));
    const { data } = yield call(NetworkService.makeAPIGetRequest, [BrandAdmin, FileManager, Directories], options);
    if (!params.parentPath) {
      yield put(changeFileManagerBrandId(params.brandId));
      yield put(saveBaseDirectories(data));
    } else {
      yield put(saveDirectories(data));
    }
    yield put(changeAllDirectoriesLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeAllDirectoriesLoadingState(false));
  }
}

function* getFileTypeGroups() {
  try {
    const { BrandAdmin, FileManager, FileTypeGroups } = AppConstants.api;
    yield put(changeFileManagerLoadingState(true));
    const response = yield call(NetworkService.makeAPIGetRequest, [BrandAdmin, FileManager, FileTypeGroups]);
    yield put(getFileTypeGroupsSuccess(response.data));
    yield put(changeFileManagerLoadingState(false));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeFileManagerLoadingState(false));
  }
}

function* renamePublicFile({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeFileManagerLoadingState(true));
    const { BrandAdmin, FileManager, RenamePublicFile } = AppConstants.api;
    yield call(NetworkService.makeAPIPutRequest, [BrandAdmin, FileManager, RenamePublicFile], options);
    yield put(changeFileManagerLoadingState(false));
    yield put(setSuccessMessage(getMessage(i18n.t('cms:file'), i18n.t('notification:slRenamed'))));
    yield put(changeModalVisibility(false));
    yield put(changeEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeFileManagerLoadingState(false));
  }
}

function* renamePublicDirectory({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeFileManagerLoadingState(true));
    const { BrandAdmin, FileManager, RenamePublicDirectory } = AppConstants.api;
    yield call(NetworkService.makeAPIPutRequest, [BrandAdmin, FileManager, RenamePublicDirectory], options);
    yield put(changeFileManagerLoadingState(false));
    yield put(setSuccessMessage(getMessage(i18n.t('cms:folder'), i18n.t('notification:slRenamed'))));
    yield put(changeModalVisibility(false));
    yield put(changeDirectoriesEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeFileManagerLoadingState(false));
  }
}

function* deletePublicFiles({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeFileManagerLoadingState(true));
    const { BrandAdmin, FileManager, DeleteFiles } = AppConstants.api;
    yield call(NetworkService.makeAPIDeleteRequest, [BrandAdmin, FileManager, DeleteFiles], options);
    yield put(changeModalVisibility(false));
    yield put(changeFileManagerLoadingState(false));
    yield put(
      setSuccessMessage(
        getMessage(i18n.t(payload.data.filePaths.length === 1 ? 'cms:theFile' : 'cms:files'), i18n.t(payload.data.filePaths.length === 1 ? 'notification:slDeleted' : 'notification:plDeleted')),
      ),
    );
    yield put(changeEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeFileManagerLoadingState(false));
  }
}

function* deletePublicDirectory({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeFileManagerLoadingState(true));
    const { BrandAdmin, FileManager, DeletePublicDirectory } = AppConstants.api;
    yield call(NetworkService.makeAPIDeleteRequest, [BrandAdmin, FileManager, DeletePublicDirectory], options);
    yield put(changeModalVisibility(false));
    yield put(changeFileManagerLoadingState(false));
    yield put(changeFileManagerLoadingState(false));
    yield put(setSuccessMessage(getMessage(i18n.t('cms:folder'), i18n.t('notification:slDeleted'))));
    yield put(changeDirectoriesEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeFileManagerLoadingState(false));
  }
}

function* createDirectory({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    yield put(changeFileManagerLoadingState(true));
    const { BrandAdmin, FileManager, Directories } = AppConstants.api;
    yield call(NetworkService.makeAPIPostRequest, [BrandAdmin, FileManager, Directories], options);
    yield put(changeFileManagerLoadingState(false));
    yield put(setSuccessMessage(getMessage(i18n.t('cms:folder'), i18n.t('notification:slCreated'))));
    yield put(changeModalVisibility(false));
    yield put(changeDirectoriesEndedStatus(true));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeFileManagerLoadingState(false));
  }
}

function* movePublicFiles({ payload }) {
  try {
    const options = {
      data: payload.data,
    };
    const { filePaths } = payload.data;
    yield put(changeFileManagerLoadingState(true));
    const { BrandAdmin, FileManager, MovePublicFiles } = AppConstants.api;
    yield call(NetworkService.makeAPIPostRequest, [BrandAdmin, FileManager, MovePublicFiles], options);
    yield put(changeFileManagerLoadingState(false));
    yield put(changeModalVisibility(false));
    yield put(changePopoverVisibility(false));
    yield put(changeEndedStatus(true));
    yield put(setSuccessMessage(getMessage(i18n.t(filePaths.length > 1 ? 'cms:files' : 'cms:file'), i18n.t(filePaths.length > 1 ? 'notification:plMoved' : 'notification:slMoved'))));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeFileManagerLoadingState(false));
  }
}

function* getFilesByPaths({ payload }) {
  try {
    const options = {
      params: payload.params,
      paramsSerializer: (param) => qs.stringify(param),
    };
    yield put(changeFileManagerLoadingState(true));
    const { BrandAdmin, FileManager, Files } = AppConstants.api;
    const { data } = yield call(NetworkService.makeAPIGetRequest, [BrandAdmin, FileManager, Files], options);
    yield put(changeFileManagerLoadingState(false));
    yield put(saveFilesByPaths(data));
  } catch (err) {
    if (err && !isJSON(err.message)) {
      const error = getError(err);
      yield put(setErrorMessage(error));
    }
    yield put(changeFileManagerLoadingState(false));
  }
}

export default function* fileManagerSaga() {
  yield all([
    takeLatest(FileManagerActionType.GET_DIRECTORIES, getDirectories),
    takeLatest(FileManagerActionType.GET_FILE_TYPE_GROUPS, getFileTypeGroups),
    takeLatest(FileManagerActionType.RENAME_PUBLIC_FILE, renamePublicFile),
    takeLatest(FileManagerActionType.DELETE_PUBLIC_FILES, deletePublicFiles),
    takeLatest(FileManagerActionType.RENAME_PUBLIC_DIRECTORY, renamePublicDirectory),
    takeLatest(FileManagerActionType.DELETE_PUBLIC_DIRECTORY, deletePublicDirectory),
    takeLatest(FileManagerActionType.CREATE_DIRECTORY, createDirectory),
    takeLatest(FileManagerActionType.MOVE_PUBLIC_FILES, movePublicFiles),
    takeLatest(FileManagerActionType.GET_FILES_BY_PATHS, getFilesByPaths),
  ]);
}
